/**
 * dateUtils.js
 *
 * Shared date formatting and calculation utilities used across dashboards and pages
 * Provides consistent date handling for Dashboard, ManagerDashboard, AdminDashboard, etc.
 */

/**
 * Format a date string to a human-readable format
 * @param {string} dateString - ISO date string or Date object
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return 'N/A';

  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options
  };

  try {
    return new Date(dateString).toLocaleDateString('en-US', defaultOptions);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

/**
 * Calculate days since a date and return human-readable text
 * @param {string} dateString - ISO date string
 * @returns {string} Human-readable time difference (e.g., "5 days ago", "Today")
 */
export const getDaysSince = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    const today = new Date();
    const days = Math.floor((today - date) / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    if (days < 0) return 'Future date'; // Handle future dates
    return `${days} days ago`;
  } catch (error) {
    console.error('Error calculating days since:', error);
    return 'N/A';
  }
};

/**
 * Calculate numeric days since a date (for comparisons and displays)
 * @param {string} dateString - ISO date string
 * @returns {number} Number of days since the date (0 for today, 1+ for past dates)
 *
 * IMPORTANT: Uses Math.ceil() to count partial days as full days
 * Example: If breakdown occurred on Jan 2 at 10 AM and today is Jan 3 at 9 AM,
 * it's still "1 day" since the breakdown, even though < 24 hours have passed.
 * This matches user expectations: "reported on Monday, it's now Tuesday = 1 day"
 */
export const getDaysSinceNumeric = (dateString) => {
  if (!dateString) return 0;

  try {
    const date = new Date(dateString);
    const today = new Date();
    // Reset time to midnight for both dates to get accurate day count
    const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Calculate difference in milliseconds then convert to days
    const diffTime = endDate - startDate;
    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, days); // Return 0 for today or future dates
  } catch (error) {
    console.error('Error calculating days since (numeric):', error);
    return 0;
  }
};

/**
 * Get the current week date range (Monday to Sunday, ISO week)
 * @returns {string} Date range string (e.g., "Dec 30 - Jan 5")
 */
export const getWeekDateRange = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  // Calculate Monday of current week (go back to Monday if not already on Monday)
  const monday = new Date(today);
  const diff = monday.getDate() - monday.getDay() + (dayOfWeek === 0 ? -6 : 1); // Adjust when Sunday
  monday.setDate(diff);

  // Calculate Sunday of current week
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);

  // Format dates as "Mon, Dec 30" or similar
  const mondayStr = monday.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const sundayStr = sunday.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return `${mondayStr} - ${sundayStr}`;
};

/**
 * Get current month and year string
 * @returns {string} Month and year (e.g., "Jan 2024")
 */
export const getCurrentMonthYear = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[new Date().getMonth()];
  const year = new Date().getFullYear();
  return `${month} ${year}`;
};

/**
 * Get current month short name
 * @returns {string} Month abbreviation (e.g., "Jan")
 */
export const getCurrentMonthShort = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[new Date().getMonth()];
};

/**
 * Calculate days between two dates
 * @param {string} startDate - ISO date string
 * @param {string} endDate - ISO date string
 * @returns {number} Number of days between dates
 */
export const daysBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return null;

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.floor((end - start) / (1000 * 60 * 60 * 24));
  } catch (error) {
    console.error('Error calculating days between:', error);
    return null;
  }
};

/**
 * Check if a date is within N days
 * @param {string} dateString - ISO date string
 * @param {number} days - Number of days to check
 * @returns {boolean} True if date is within N days from now
 */
export const isWithinDays = (dateString, days) => {
  if (!dateString || days === undefined) return false;

  try {
    const date = new Date(dateString);
    const today = new Date();
    const daysFromNow = Math.floor((date - today) / (1000 * 60 * 60 * 24));
    return daysFromNow >= 0 && daysFromNow <= days;
  } catch (error) {
    console.error('Error checking if within days:', error);
    return false;
  }
};

/**
 * Format date for input fields (YYYY-MM-DD)
 * @param {string|Date} dateString - Date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
/**
 * Anchor date for device age (matches backend: installation → purchase → created).
 * @param {object} device
 * @returns {string|null}
 */
export const getDeviceAgeAnchorDate = (device) => {
  if (!device) return null;
  return device.installation_date || device.purchase_date || device.created_at || null;
};

/**
 * Device age in whole years and remaining days (matches Device View / list engine).
 * @param {object|string} deviceOrDateString - Device row or ISO date string
 * @returns {{ years: number, days: number, totalDays: number }|null}
 */
export const getDeviceAgeParts = (deviceOrDateString) => {
  const dateString = typeof deviceOrDateString === 'object'
    ? getDeviceAgeAnchorDate(deviceOrDateString)
    : deviceOrDateString;
  if (!dateString) return null;

  try {
    const anchor = new Date(dateString);
    const today = new Date();
    anchor.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const totalDays = Math.max(0, Math.floor((today - anchor) / (1000 * 60 * 60 * 24)));
    return {
      years: Math.floor(totalDays / 365),
      days: totalDays % 365,
      totalDays,
    };
  } catch (error) {
    console.error('Error calculating device age:', error);
    return null;
  }
};

/**
 * Human-readable device age (e.g. "3 years, 45 days").
 * @param {number} years
 * @param {number} days
 * @returns {string}
 */
export const formatDeviceAgeText = (years, days) => {
  const y = Math.max(0, Number(years) || 0);
  const d = Math.max(0, Number(days) || 0);
  if (y === 0 && d === 0) return 'Under 1 year';
  if (y === 0) return d === 1 ? '1 day' : `${d} days`;
  const yearPart = y === 1 ? '1 year' : `${y} years`;
  if (d === 0) return yearPart;
  const dayPart = d === 1 ? '1 day' : `${d} days`;
  return `${yearPart}, ${dayPart}`;
};

export const formatDateForInput = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date for input:', error);
    return '';
  }
};
